import { useRef, useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useChat } from "@/hooks/use-chat";
import { useChatSidebar } from "@/contexts/ChatSidebarContext";
import { MessageList } from "./chat/MessageList";
import { ChatInput } from "./chat/ChatInput";
import { QueryLimitAlert } from "./chat/QueryLimitAlert";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, ChevronDown, ArrowRight, ChevronLeft, ChevronRight, PlusIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

// Import the questions from the AskStrykrAICard component
const generateMarketContextQuestions = () => {
  return [
    // Asset-specific questions
    "What's the forecast for AAPL stock in the next quarter?",
    "How will Tesla stock perform with recent EV market changes?",
    "What factors are driving Bitcoin price movements this month?",
    "Should I invest in NVIDIA given AI chip demand?",
    "What's the outlook for gold prices with current inflation trends?",
    "How is Amazon stock likely to perform this quarter?",
    "What's the forecast for Microsoft stock with AI developments?",
    "How are interest rates affecting bank stocks like JPMorgan?",
    
    // Country-specific questions
    "How are Japan's monetary policies affecting the Nikkei index?",
    "What impact will China's economic data have on global markets?",
    "How is the UK economy performing post-Brexit?",
    "What's the outlook for German manufacturing sector?",
    "How are India's tech stocks performing this quarter?",
    "What impact will US inflation have on the dollar?",
    
    // Sector/category questions
    "What are the key factors influencing the S&P 500 this week?",
    "Which energy stocks will benefit from current oil prices?",
    "How is the semiconductor sector performing against market trends?",
    "What's the outlook for REITs with current interest rates?",
    "Which healthcare stocks show the most promise this year?",
    "How will AI affect the tech sector in the next quarter?"
  ];
};

interface AiChatProps {
  initialQuestion?: string;
}

export const AiChat = ({ initialQuestion }: AiChatProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get('conversationId');
  const { pendingContent, clearPendingContent } = useChatSidebar();
  const { 
    messages, 
    isLoading, 
    sendMessage, 
    enableBellwether,
    setEnableBellwether,
    enableMacro,
    setEnableMacro,
    canAskQuestion,
    remainingQueries,
    hasReachedLimit,
    isSubscribed,
    isTrialing,
    startNewChat,
    setMessages // Add setMessages to access this method
  } = useChat();

  const [inputValue, setInputValue] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);
  const [userScrolled, setUserScrolled] = useState(false);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const [showScrollButton, setShowScrollButton] = useState(false);
  const scrollElement = useRef<HTMLElement | null>(null);
  
  // Refs for throttling suggested prompt handling
  const lastHandledPrompt = useRef<string>('');
  const lastHandledTime = useRef<number>(0);

  // Questions carousel state with randomization
  const [allQuestions] = useState(() => {
    // Get all questions and shuffle them
    const questions = generateMarketContextQuestions();
    return [...questions].sort(() => Math.random() - 0.5); // Randomize order
  });
  const [visibleQuestions, setVisibleQuestions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const questionsPerPage = 2; // Display two questions at a time
  
  // Set up visible questions based on current page
  useEffect(() => {
    const startIdx = currentPage * questionsPerPage;
    setVisibleQuestions(allQuestions.slice(startIdx, startIdx + questionsPerPage));
  }, [currentPage, allQuestions]);
  
  // Rotate questions every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % Math.ceil(allQuestions.length / questionsPerPage));
    }, 30000);
    return () => clearInterval(interval);
  }, [allQuestions.length]);
  
  // Handle manual navigation for questions carousel
  const nextPage = () => {
    setCurrentPage(prev => (prev + 1) % Math.ceil(allQuestions.length / questionsPerPage));
  };
  
  const prevPage = () => {
    setCurrentPage(prev => 
      prev === 0 ? Math.ceil(allQuestions.length / questionsPerPage) - 1 : prev - 1
    );
  };
  
  // Auto-populate and submit if initialQuestion is provided
  const hasSentInitial = useRef(false);
  useEffect(() => {
    if (hasSentInitial.current) return;
    
    // Only process initial question if no conversationId is present in URL
    if (!conversationId && initialQuestion && initialQuestion.trim()) {
      setInputValue(initialQuestion);
      setHasInteracted(true);
      sendMessage(initialQuestion);
      hasSentInitial.current = true;
    } else if (!conversationId) {
      // Check session storage for pending questions
      const sessionQuestion = sessionStorage.getItem("strykr-initial-question");
      if (sessionQuestion) {
        setInputValue(sessionQuestion);
        setHasInteracted(true);
        sendMessage(sessionQuestion);
        hasSentInitial.current = true;
        sessionStorage.removeItem("strykr-initial-question");
      }
    }
  }, [initialQuestion, conversationId, sendMessage]);

  // Show query limit information only for free users (not subscribed and not in trial)
  const showQueryLimits = !isSubscribed && !isTrialing;


  // Get scroll viewport element reference on mount
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollElement.current = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
    }
  }, []);

  // Memoize scroll function to prevent recreation on renders
  const scrollToLatestMessage = useCallback(() => {
    if (scrollElement.current) {
      // Smoothly scroll to bottom
      scrollElement.current.scrollTo({
        top: scrollElement.current.scrollHeight,
        behavior: 'smooth'
      });
      
      // Reset states only after scroll animation completes
      setTimeout(() => {
        setUserScrolled(false);
        setShowScrollButton(false);
      }, 300); // Match duration with CSS transition
    }
  }, []);

  // Handle scroll events
  useEffect(() => {
    const element = scrollElement.current;
    if (!element) return;

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Use requestAnimationFrame for better performance with scroll events
      requestAnimationFrame(() => {
        if (!element) return;
        
        const { scrollTop, scrollHeight, clientHeight } = element;
        const scrollBottom = scrollHeight - clientHeight - scrollTop;
        const threshold = isMobile ? 100 : 50;
        const isAtBottom = scrollBottom < threshold;
        
        // Only update state when there's an actual change
        if (!isAtBottom !== userScrolled) {
          setUserScrolled(!isAtBottom);
        }
        
        // Always show scroll button when not at bottom and have messages
        // This ensures the button is visible when new content arrives
        if (messages.length > 0 && !isAtBottom) {
          setShowScrollButton(true);
        } else {
          setShowScrollButton(false);
        }
      });
    };

    element.addEventListener('scroll', handleScroll);
    return () => {
      element.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isMobile, userScrolled, showScrollButton, messages.length]);

  // We're no longer auto-scrolling when messages change
  // This allows users to read messages as they come in and scroll down manually
  useEffect(() => {
    // Only auto-scroll on the very first message (initial greeting)
    // or when the user sends a message (not when AI responds)
    if (messages.length === 1 || (messages.length > 0 && messages[messages.length - 1].role === 'user')) {
      scrollToLatestMessage();
    }
  }, [messages.length, scrollToLatestMessage]);

  useEffect(() => {
    const handleStreamUpdate = () => {
      // We no longer auto-scroll during streaming updates
      // This allows users to read messages as they come in
      // The scroll button will be visible if they want to scroll down
    };

    window.addEventListener('stream-update', handleStreamUpdate);
    return () => {
      window.removeEventListener('stream-update', handleStreamUpdate);
    };
  }, []);

  // Handle pending content from sidebar context
  useEffect(() => {
    if (pendingContent && !isLoading) {
      setInputValue(pendingContent);
      setHasInteracted(true);
      clearPendingContent();
      // Auto-focus the input so user can easily modify or send
      setTimeout(() => {
        const inputElement = document.querySelector('textarea[placeholder*="Ask"]') as HTMLTextAreaElement;
        if (inputElement) {
          inputElement.focus();
          inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
        }
      }, 100);
    }
  }, [pendingContent, isLoading, clearPendingContent]);

  // Send message handler, wrapped in useCallback for stable reference
  const handleSendMessage = useCallback(async (message: string) => {
    // Enhanced input validation
    if (!message.trim()) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Please enter a message'
      });
      return;
    }
    // Size limits to prevent DoS attacks
    if (message.length > 3000) {
      toast({
        variant: 'destructive',
        title: 'Message Too Long',
        description: 'Please shorten your message.'
      });
      return;
    }
    setInputValue("");
    setHasInteracted(true);
    await sendMessage(message);
  }, [sendMessage]);

  // Handle suggested prompts with throttling - moved after handleSendMessage
  useEffect(() => {
    const handleSuggestedPrompt = (event: CustomEvent<{ prompt: string }>) => {
      const now = Date.now();
      const prompt = event.detail.prompt;
      
      // Throttle: only handle if it's a different prompt or enough time has passed (5 seconds)
      if (prompt === lastHandledPrompt.current && now - lastHandledTime.current < 5000) {
        return;
      }
      
      if (canAskQuestion && !isLoading) {
        console.log("[AiChat] Handling suggested prompt with toggles:", {
          enableBellwether,
          enableMacro,
          prompt
        });
        lastHandledPrompt.current = prompt;
        lastHandledTime.current = now;
        handleSendMessage(prompt);
      }
    };

    window.addEventListener('useSuggestedPrompt', handleSuggestedPrompt as EventListener);
    return () => {
      window.removeEventListener('useSuggestedPrompt', handleSuggestedPrompt as EventListener);
    };
  }, [canAskQuestion, isLoading, enableBellwether, enableMacro, handleSendMessage]);

  // Submit handler for chat input
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSendMessage(inputValue);
  };

  // Log initialQuestion changes only when it actually changes
  useEffect(() => {
    console.log("[AiChat] initialQuestion:", initialQuestion);
  }, [initialQuestion]);

  return (
    <div className="flex flex-col h-full w-full overflow-hidden relative mobile-safe">
      {/* New Chat Button - positioned absolute in top right */}
      <div className="absolute top-3 right-3 z-20">
        <Button
          onClick={() => {
            startNewChat();
            setHasInteracted(false);
            setInputValue("");
            setUserScrolled(false);
          }}
          className="flex items-center space-x-1 bg-[#0c1221]/90 text-white text-sm font-medium px-2.5 py-1.5 rounded-lg shadow-sm border border-white/10 hover:bg-[#162038] transition-colors backdrop-blur-sm"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          <span className="text-xs">New</span>
        </Button>
      </div>
      {/* Main chat container with messages */}
      <div 
        className={cn(
          "flex flex-col flex-1 overflow-hidden h-full transition-all duration-500 ease-in-out",
          hasInteracted || messages.length > 1 ? "pb-32" : "pb-20"
        )}
      >
        {/* Query limits banner for free users */}
        {showQueryLimits && (
          <div className="sticky top-0 w-full flex items-center justify-between p-2 border-b border-white/10 bg-black/60 backdrop-blur-md z-[1]">
            <div className="flex items-center space-x-2">
              <span className="text-xs text-white/70">Daily queries:</span>
              {hasReachedLimit ? (
                <Badge variant="destructive" className="text-xs">Limit reached</Badge>
              ) : (
                <Badge variant={remainingQueries <= 2 ? "outline" : "secondary"} className="text-xs">
                  {5 - remainingQueries} / 5 used
                </Badge>
              )}
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-1 text-xs text-white/70 cursor-help">
                    <AlertCircle className="h-3 w-3" />
                    <span>Limits apply</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="absolute top-6 left-[-10rem] w-[12.5rem]">
                  <p className="text-xs">Free users are limited to 5 queries per day.</p>
                  <p className="text-xs">Subscribe for unlimited access.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        
        {/* Scrollable message area with padding to prevent overlap with New Chat button */}
        <ScrollArea 
          ref={scrollAreaRef} 
          className="flex-1 h-full overflow-auto scrollbar-gradient"
        >
          <div className="pt-12"> {/* Reduced padding at the top */}
            <MessageList 
              messages={messages}
              isLoading={isLoading}
              canAskQuestion={canAskQuestion}
              hasInteracted={hasInteracted}
            />
          </div>
          
          {/* Strykr logo should always appear when there's no interaction */}
          {!hasInteracted && (
            <div className="w-full flex justify-center items-center pt-16 pb-6">
              <div className="flex flex-col items-center justify-center">
                <div className="h-16 w-16 rounded-full p-[2px] bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center shadow-md shadow-indigo-500/20">
                  <img 
                    src="/strykr-logo.jpeg" 
                    alt="Strykr AI" 
                    className="h-full w-full rounded-full" 
                  />
                </div>
                <h1 className="text-2xl font-semibold text-white mt-3">Strykr AI</h1>
                <p className="text-gray-300 text-sm mt-2">Your financial market analysis assistant</p>
              </div>
            </div>
          )}

          {/* Suggested prompts when user hasn't interacted */}
          {!hasInteracted && (
            <div className="w-full flex justify-center items-center py-2">
              <div className="max-w-4xl w-full px-4 md:px-6">
                <div className="text-center w-full mb-4">
                  <p className="text-gray-400 text-sm">Suggested questions</p>
                </div>
                <div className="w-full space-y-2">
                  {visibleQuestions.map((question, index) => (
                    <button
                      key={index}
                      className="w-full py-2.5 px-3.5 text-left text-white/90 text-sm bg-[#0c1221]/80 rounded-lg border border-white/5 hover:bg-[#162038]/80 hover:border-indigo-500/20 transition-all duration-200 leading-relaxed"
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('useSuggestedPrompt', {
                          detail: { prompt: question }
                        }));
                      }}
                    >
                      {question}
                    </button>
                  ))}
                </div>
                
                {/* Carousel navigation */}
                <div className="flex items-center justify-center space-x-6 mt-4">
                  <button 
                    className="h-7 w-7 rounded-full bg-black/20 flex items-center justify-center hover:bg-black/30 transition-colors"
                    onClick={prevPage}
                  >
                    <ChevronLeft className="h-3.5 w-3.5 text-white/70" />
                  </button>
                  <span className="text-xs text-gray-500">{currentPage + 1}/{Math.ceil(allQuestions.length / questionsPerPage)}</span>
                  <button 
                    className="h-7 w-7 rounded-full bg-black/20 flex items-center justify-center hover:bg-black/30 transition-colors"
                    onClick={nextPage}
                  >
                    <ChevronRight className="h-3.5 w-3.5 text-white/70" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </ScrollArea>
        
        {/* Scroll down button with improved styling and animation */}
        {showScrollButton && (
          <button
            onClick={scrollToLatestMessage}
            className="absolute bottom-[120px] left-1/2 transform -translate-x-1/2 z-10 p-2.5 rounded-full 
                     bg-gradient-to-br from-blue-600 to-indigo-600 text-white 
                     shadow-lg shadow-indigo-900/30 hover:shadow-xl hover:from-blue-600 hover:to-purple-600
                     transition-all duration-300 animate-fade-in flex items-center justify-center
                     border border-indigo-400/30"
            aria-label="Scroll to bottom"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {/* Fixed input at bottom */}
      <div className="absolute bottom-0 left-0 right-0 w-full bg-gradient-to-t from-background via-background/95 to-background/50 backdrop-blur-sm pt-6">
        {showQueryLimits && hasReachedLimit && (
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <QueryLimitAlert
              remainingQueries={remainingQueries}
              hasReachedLimit={hasReachedLimit}
              maxQueries={5}
            />
          </div>
        )}
        
        <ChatInput 
          value={inputValue}
          onChange={setInputValue}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          canAskQuestion={canAskQuestion}
          hasInteracted={hasInteracted}
          enableBellwether={enableBellwether}
          onBellwetherChange={setEnableBellwether}
          enableMacro={enableMacro}
          onMacroChange={setEnableMacro}
        />
      </div>
    </div>
  );
};

